import { createFileRoute, Link } from '@tanstack/react-router';
import { Eyebrow } from '@leanin/ui';
import { Body, Heading } from '@leanin/ui/server';
import { isUuid } from '@leanin/shared';
import { ContestSubmitForm } from '@/components/moderation/ContestSubmitForm';
import { ErrorState } from '@leanin/ui';
import styles from './page.module.css';

type TargetKind = 'post' | 'comment' | 'message' | 'event' | 'resource' | 'member';

const TARGET_KINDS: ReadonlyArray<TargetKind> = [
  'post',
  'comment',
  'message',
  'event',
  'resource',
  'member',
];

const isTargetKind = (value: string): value is TargetKind =>
  (TARGET_KINDS as ReadonlyArray<string>).includes(value);

type Search = {
  targetKind?: TargetKind;
  targetId?: string;
  holdId?: string;
};

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute('/_authed/moderation/contest/')({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const kind =
      typeof raw.targetKind === 'string' && isTargetKind(raw.targetKind)
        ? (raw.targetKind as TargetKind)
        : undefined;
    return {
      ...(kind !== undefined ? { targetKind: kind } : {}),
      ...(pickStr(raw.targetId) ? { targetId: pickStr(raw.targetId) as string } : {}),
      ...(pickStr(raw.holdId) ? { holdId: pickStr(raw.holdId) as string } : {}),
    };
  },
  component: ContestSubmitPage,
});

function ContestSubmitPage() {
  const params = Route.useSearch();
  const targetKind = params.targetKind ?? null;
  const targetId =
    params.targetId !== undefined && isUuid(params.targetId)
      ? params.targetId
      : null;

  if (targetKind === null || targetId === null) {
    return (
      <ErrorState
        title="Missing context for this contestation."
        body="Open this page from the auto-hide notice you received — that link includes the target identifiers required to file a contestation."
        reference="moderation/contest_missing_context"
      />
    );
  }

  return (
    <div className={styles.page}>
      <Link
        to={
          params.holdId
            ? '/moderation/auto-hide-notice/$holdId'
            : '/feed'
        }
        {...(params.holdId ? { params: { holdId: params.holdId } } : {})}
        className={styles.back}
      >
        ‹ Hidden post
      </Link>
      <div className={styles.eyebrowSpacer}>
        <Eyebrow>Contest moderation decision</Eyebrow>
      </div>
      <Heading level={1} className={styles.heading}>Tell us why this should stay up.</Heading>
      <Body className={styles.lede}>
        A senior moderator (different from the one who reviewed initially) will read your
        contestation and make the final call within 5 business days. Be specific.
      </Body>

      <div className={styles.contextCard}>
        <div className={styles.contextLabel}>Contesting</div>
        <div className={styles.contextValue}>
          {targetKind} · {targetId.slice(0, 8)}
        </div>
        <div className={styles.contextSecondary}>
          Your post stays hidden while the senior moderator reviews.
        </div>
      </div>

      <ContestSubmitForm
        targetKind={targetKind}
        targetId={targetId}
        holdId={params.holdId}
      />
    </div>
  );
}
